import React from 'react';
import './Modal.css';

/**
 * Reusable Modal Component
 * 
 * A flexible modal component that can display different types of messages
 * (error, success, warning, info) with customizable content.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback function when modal is closed
 * @param {string} props.type - Type of modal: 'error', 'success', 'warning', 'info'
 * @param {string} props.title - Modal title (optional, defaults based on type)
 * @param {string|React.ReactNode} props.message - Message content (can be string or React component)
 * @param {string} props.closeButtonText - Text for close button (optional, defaults to "Close")
 * @param {function} props.onConfirm - Optional callback for confirm action (creates second button)
 * @param {string} props.confirmButtonText - Text for confirm button (default: "Confirm")
 * @param {string} props.cancelButtonText - Text for cancel button (default: "Cancel")
 */
const Modal = ({
  isOpen,
  onClose,
  type = 'info',
  title,
  message,
  closeButtonText = 'Close',
  onConfirm,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
}) => {
  if (!isOpen) return null;

  // Default titles based on type
  const defaultTitles = {
    error: 'Error',
    success: 'Success!',
    warning: 'Warning',
    info: 'Information',
  };

  const modalTitle = title || defaultTitles[type] || 'Notice';

  // Icons for different types
  const icons = {
    error: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="#e74c3c" strokeWidth="2" fill="rgba(231, 76, 60, 0.1)"/>
        <path d="M12 8V12M12 16H12.01" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    success: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="#27ae60" strokeWidth="2" fill="rgba(39, 174, 96, 0.1)"/>
        <path d="M8 12L11 15L16 9" stroke="#27ae60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    warning: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 20H22L12 2Z" stroke="#f39c12" strokeWidth="2" fill="rgba(243, 156, 18, 0.1)" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 9V13M12 17H12.01" stroke="#f39c12" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    info: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="#3498db" strokeWidth="2" fill="rgba(52, 152, 219, 0.1)"/>
        <path d="M12 16V12M12 8H12.01" stroke="#3498db" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <div className={`modal-overlay modal-overlay-${type}`} onClick={handleOverlayClick}>
      <div className={`modal modal-${type}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-icon">
          {icons[type] || icons.info}
        </div>
        <h3 className={`modal-title modal-title-${type}`}>{modalTitle}</h3>
        <div className="modal-message">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>
        <div className="modal-actions">
          {onConfirm ? (
            <>
              <button 
                className={`modal-button modal-button-secondary`} 
                onClick={onClose}
              >
                {cancelButtonText}
              </button>
              <button 
                className={`modal-button modal-button-${type}`} 
                onClick={handleConfirm}
              >
                {confirmButtonText}
              </button>
            </>
          ) : (
            <button 
              className={`modal-button modal-button-${type}`} 
              onClick={onClose}
            >
              {closeButtonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;

